import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { PackageInput } from '@/types/packages';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Plus, Trash2 } from 'lucide-react';
import { getSupabaseClient } from '@/lib/env';

export default function CreateCustomPackage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<PackageInput>({
    title: '',
    price_cents: 0,
    description: '',
    features: [],
    estimated_turnaround_days: 0,
  });

  const handleAddFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [
        ...prev.features,
        { title: '', description: '', sort_order: prev.features.length }
      ]
    }));
  };

  const handleRemoveFeature = (index: number) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleFeatureChange = (index: number, field: 'title' | 'description', value: string) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.map((feature, i) => 
        i === index ? { ...feature, [field]: value } : feature
      )
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsSubmitting(true);
    try {
      const supabase = await getSupabaseClient();
      
      // Create the custom package
      const { data: newPackage, error: packageError } = await supabase
        .from('custom_packages')
        .insert({
          seller_id: user.id,
          title: formData.title,
          price_cents: formData.price_cents,
          description: formData.description,
          estimated_turnaround_days: formData.estimated_turnaround_days,
        })
        .select()
        .single();

      if (packageError) throw packageError;

      // Create the features
      const { error: featuresError } = await supabase
        .from('package_features')
        .insert(
          formData.features.map(feature => ({
            package_id: newPackage.id,
            title: feature.title,
            description: feature.description,
            sort_order: feature.sort_order,
          }))
        );

      if (featuresError) throw featuresError;

      toast({
        title: 'Success',
        description: 'Custom package created successfully',
      });

      navigate('/seller-dashboard');
    } catch (error) {
      console.error('Error creating custom package:', error);
      toast({
        title: 'Error',
        description: 'Failed to create custom package',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto py-8">
      <Card>
        <CardHeader>
          <CardTitle>Create Custom Package</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Package Title</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Premium Link Building Package"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Price (in cents)</label>
                <Input
                  type="number"
                  value={formData.price_cents}
                  onChange={(e) => setFormData(prev => ({ ...prev, price_cents: parseInt(e.target.value) }))}
                  placeholder="e.g., 299900 for $2,999"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe your package..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Estimated Turnaround (days)</label>
                <Input
                  type="number"
                  value={formData.estimated_turnaround_days}
                  onChange={(e) => setFormData(prev => ({ ...prev, estimated_turnaround_days: parseInt(e.target.value) }))}
                  required
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium">Package Features</label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddFeature}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Feature
                  </Button>
                </div>

                <div className="space-y-4">
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex gap-4 items-start">
                      <div className="flex-1 space-y-2">
                        <Input
                          value={feature.title}
                          onChange={(e) => handleFeatureChange(index, 'title', e.target.value)}
                          placeholder="Feature title"
                          required
                        />
                        <Textarea
                          value={feature.description}
                          onChange={(e) => handleFeatureChange(index, 'description', e.target.value)}
                          placeholder="Feature description (optional)"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveFeature(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Package'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
} 